<template>
  <hb-basic-page
    :back-button="{ show: true, fallback: 'tenant_surveys' }"
    :loading="loading"
    :subtle-loading="subtleLoading"
    :title="!loading ? $t('surveys.type.' + survey.surveyTemplate.type) : ''"
    name="single-survey-view"
  >
    <v-col v-if="!loading" cols="12" lg="8" md="8" offset-lg="2" offset-md="2" offset-xl="3" xl="6">
      <v-row>

        <v-col cols="12">
          <v-card>
            <v-card-title>
              <section-header icon="mdi mdi-calendar">
                {{ $t('surveys.schedule') }}
              </section-header>
            </v-card-title>
            <v-card-text class="text-body-2 text-sm-body-1">
              <v-calendar
                ref="calendar"
                :events="[{start: new Date(survey.start), end: new Date(survey.end), color: 'accent2'}]"
                :value="new Date(survey.start)"
                category-show-all
                color="accent"
                disabled
                style="background-color: transparent;"
                type="month"
              ></v-calendar>
            </v-card-text>

            <v-card-text v-if="surveyEnded" class="text-body-2 text-sm-body-1 info--text">
              <v-icon color="info">mdi mdi-information-outline</v-icon>
              {{ $t('surveys.answering_is_closed') }}
            </v-card-text>
            <v-card-text v-if="!surveyEnded">
              <div class="subtitle-2">
                <span>{{ $t('surveys.chosen_dates') }}: </span> {{
                  $formatDateNoTime(survey.start)
                }} -
                {{ $formatDateNoTime(survey.end) }}
              </div>
            </v-card-text>

            <v-card-actions v-if="!readonly"
                            :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''">
              <basic-button :block="$vuetify.breakpoint.xsOnly"
                            :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                            :disabled="loading || endingSurvey"
                            :outlined="$vuetify.breakpoint.xsOnly"
                            color="primary"
                            text
                            @click="editSchedule">
                <v-icon v-if="$icons" left>{{ surveyEnded ? 'mdi mdi-lock-clock' : 'edit' }}</v-icon>
                {{ surveyEnded ? $t('surveys.reopen_answering') : $t('edit') }}
              </basic-button>
              <basic-button v-if="!surveyEnded"
                            :block="$vuetify.breakpoint.xsOnly"
                            :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                            :disabled="loading"
                            :loading="endingSurvey"
                            :outlined="$vuetify.breakpoint.xsOnly"
                            color="warning"
                            text
                            @click="endSurvey">
                <v-icon left>mdi mdi-stop-circle-outline</v-icon>
                {{ $t('surveys.end_survey') }}
              </basic-button>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col v-if="!surveyEnded" cols="12">
          <share-survey-card :survey="survey" :teams="teams" @reload-teams="reloadTeams"/>
        </v-col>
        <v-col v-else cols="12">
          <list-survey-teams-card :survey="survey"/>
        </v-col>

        <survey-interpretation-tasks-card :loading="loading" :survey="survey"/>

        <v-col cols="12">
          <v-card>
            <v-card-title>
              <section-header icon="mdi mdi-chart-box-outline">
                {{ $t('surveys.details') }}
              </section-header>
            </v-card-title>
            <v-card-text v-if="!surveyEnded" class="text-body-2 text-sm-body-1">
              {{ $t('surveys.survey_is_still_in_progress_help') }}
            </v-card-text>
            <v-card-text v-if="totalAnswers" class="text-body-2 text-sm-body-1">
              {{ $t('surveys.total_answers') }}: {{ totalAnswers }} {{ estimatedAnswerers }}
              <v-btn v-if="!readonly"
                     :title="$t('surveys.estimated_answerers_explained')" class="mt-n1" icon small
                     @click="openEditEstimatedAnswerersDialog">
                <v-icon size="16">mdi mdi-pencil</v-icon>
              </v-btn>
            </v-card-text>
            <v-card-text v-if="totalAnswers > 0">
              <survey-statistics-chart :language="$userInfo.language" :statistics="statistics" :survey="survey"/>
            </v-card-text>
            <v-card-text v-else class="text-body-2 text-sm-body-1 red--text">
              <v-icon color="red">mdi mdi-information-outline</v-icon>
              {{ $t('surveys.no_answers_yet') }}
            </v-card-text>

            <v-card-text v-if="!surveyEnded">
              <survey-notification-select :survey="survey" @change="onSurveyNotificationChange"/>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card>
            <v-card-title>
              <section-header>
                {{ $t('surveys.set_name_title') }}
              </section-header>
            </v-card-title>
            <v-card-text class="text-body-2 text-sm-body-1">
              {{ $t('surveys.set_name_desc') }}
            </v-card-text>
            <v-text-field v-model="survey.name"
                          :class="$isReadOnlyUser ? 'pb-3' : ''"
                          :dense="$vuetify.breakpoint.xsOnly"
                          :disabled="$isReadOnlyUser"
                          :label="$t('surveys.survey_name')"
                          autocomplete="off"
                          class="ma-2"
                          hide-details
                          outlined>

            </v-text-field>
            <v-card-actions v-if="!readonly" class="pb-n1">
              <basic-button :block="$vuetify.breakpoint.xsOnly"
                            :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                            :disabled="oldName === null || loading"
                            :outlined="$vuetify.breakpoint.xsOnly"
                            color="warning"
                            text
                            @click="renameSurvey(true)">
                <v-icon left>{{ 'mdi mdi-close' }}</v-icon>
                {{ $t('surveys.discard_name_btn') }}
              </basic-button>
              <basic-button :block="$vuetify.breakpoint.xsOnly"
                            :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                            :disabled="!nameChanged || loading "
                            :outlined="$vuetify.breakpoint.xsOnly"
                            color="primary"
                            text
                            @click="renameSurvey">
                {{ $t('save') }}
              </basic-button>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col v-if="!$isReadOnlyUser" cols="12">
          <basic-button :disabled="loading"
                        block
                        color="error"
                        large
                        outlined
                        @click="deleteSurvey">
            {{ $t('surveys.delete') }}
          </basic-button>
        </v-col>

        <!--
        <v-col cols="12" md="6">

          <v-card class="mb-6">
            <v-card-title>
              <v-icon color="primary" left size="32">mdi mdi-chart-box-outline</v-icon>
              LASKE, TÄMÄ LAATIKKO POISTUU
              <v-spacer></v-spacer>
              <basic-button
                :disabled="loading"
                color="error"
                small
                text
                @click="calculate">
                Laske
              </basic-button>
            </v-card-title>
            <v-card-text v-if="calculationLoading">
              <loading-indicator align-middle/>
            </v-card-text>
            <v-card-text v-if="calculateResult" class="text-body-2 text-sm-body-1" style="white-space: pre-wrap;">
              {{ calculateResultJSON }}
            </v-card-text>

          </v-card>
        </v-col>
        <v-col v-if="calculateResult && survey && survey.surveyTemplate.type === 'PERSONNEL'" cols="12" md="6">

          <v-card class="mb-6">
            <v-card-title>
              <v-icon color="primary" left size="32">mdi mdi-chart-box-outline</v-icon>
              Culture
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text v-if="calculateResult" class="text-body-2 text-sm-body-1" style="white-space: pre-wrap;">
              <v-row>
                <v-col class="mb-15" cols="12">
                  <company-culture-chart :calculation="calculateResult" :loading="calculationLoading"
                                         @refresh="calculate"/>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card v-if="calculateResult && survey && survey.surveyTemplate.type === 'PERSONNEL'" class="mb-6">
            <v-card-title>
              <v-icon color="primary" left size="32">mdi mdi-chart-box-outline</v-icon>
              CFI
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text v-if="calculateResult" class="text-body-2 text-sm-body-1" style="white-space: pre-wrap;">
              <v-row>
                <v-col class="mt-15" cols="12">
                  <company-fitness-index-chart :calculation="calculateResult" :loading="calculationLoading"
                                               @refresh="calculate"/>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        -->

      </v-row>
    </v-col>

    <edit-schedule-dialog ref="editScheduleDialog" :survey-ended="surveyEnded" @survey-saved="handleScheduleSaved"/>
    <edit-estimated-answerers-dialog ref="editEstimatedAnswerersDialog" @survey-saved="reloadSurvey"/>

  </hb-basic-page>
</template>

<script>
import surveyApi from "@/api/survey";
import teamApi from "@/api/team";
import BasicButton from "@/components/BasicButton";
import ShareSurveyCard from "@/components/Tenant/ShareSurveyCard";
import dayjs from 'dayjs';
import EditScheduleDialog from "@/components/Tenant/SingleSurvey/EditScheduleDialog";
import SurveyStatisticsChart from "@/components/Tenant/SingleSurvey/SurveyStatisticsChart";
import SectionHeader from "@/components/SectionHeader";
import SurveyNotificationSelect from "@/components/SurveyNotificationSelect";
import SurveyInterpretationTasksCard from "@/components/Tenant/SingleSurvey/SurveyInterpretationTasksCard";
import EditEstimatedAnswerersDialog from "@/components/Tenant/SingleSurvey/EditEstimatedAnswerersDialog";
import ListSurveyTeamsCard from "@/components/Tenant/ListSurveyTeamsCard";

export default {
  name: 'SingleSurveyView',
  components: {
    ListSurveyTeamsCard,
    EditEstimatedAnswerersDialog,
    SurveyInterpretationTasksCard,
    SurveyNotificationSelect,
    SectionHeader,
    SurveyStatisticsChart,
    EditScheduleDialog, ShareSurveyCard, BasicButton
  },
  props: ['id'],
  data() {
    return {
      loading: true,
      subtleLoading: false,
      teams: [],
      survey: {},
      oldName: null,
      calculateResult: null,
      calculateResultJSON: null,
      calculationLoading: false,
      statistics: [],
      endingSurvey: false,
    };
  },

  methods: {
    editSchedule() {
      this.$refs.editScheduleDialog.open([this.survey.start, this.survey.end], this.survey);
    },
    handleScheduleSaved(survey) {
      this.survey = survey;
    },
    openEditEstimatedAnswerersDialog() {
      this.$refs.editEstimatedAnswerersDialog.open(this.survey);
    },
    async endSurvey() {
      this.endingSurvey = true;

      const survey = {...this.survey};
      survey.end = dayjs().toISOString();

      try {
        const response = await surveyApi.updateSurvey(survey);
        this.survey = response;
        this.$showSuccessNotification(this.$t('surveys.survey_saved'));
      } catch (error) {
        this.$handleApiError(error, this.$t('surveys.errors.save_error'));
      }
      this.endingSurvey = false;
      this.$store.dispatch('reloadMenuCount');
    },
    async renameSurvey(discard = false) {
      const survey = {...this.survey};
      if (discard === true) {
        survey.name = null;
      } else {
        if (this.survey.name === null) {
          return;
        }
      }
      this.loading = true;
      try {
        const response = await surveyApi.updateSurvey(survey);
        this.survey = response;
        this.oldName = this.survey.name;
      } catch (error) {
        this.$handleApiError(error, this.$t('surveys.errors.save_error'));
      }
      this.loading = false;
    },
    async deleteSurvey() {
      const confirm = await this.$confirmDelete(this.$t('surveys.delete'), this.$t('surveys.delete_survey_confirm'));
      if (!confirm) return;

      try {
        await surveyApi.deleteSurvey(this.survey.key);
        this.$showSuccessNotification(this.$t('surveys.survey_deleted'));
        this.$router.replace({name: 'tenant_surveys'});
      } catch (error) {
        this.$handleApiError(error, this.$t('surveys.errors.delete_error'));
      }
      this.endingSurvey = false;
      this.$store.dispatch('reloadMenuCount');
    },
    backToSurveys() {
      this.$router.go(-1);
    },
    async reloadSurvey() {
      try {
        const response = await surveyApi.getSurvey(this.id);
        if (response) {
          this.survey = response;
          this.oldName = response.name;
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.error_loading_survey'));
      }
      try {
        this.statistics = await surveyApi.getSurveyStatistics(this.id);
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.error_loading_survey_statistics'));
      }
    },
    async reloadTeams() {
      try {
        const response = await teamApi.getTeams();
        if (response) {
          this.teams = response;
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.create_error'));
      }
    },
    async calculate() {
      this.calculationLoading = true;
      try {
        const result = await surveyApi.calculateResult(this.id);
        this.calculateResultJSON = JSON.stringify(result, null, 2);
        this.calculateResult = result;
      } catch (e) {
        console.error(e)
      }
      this.calculationLoading = false;
    },
    async getCalculation() {
      this.calculationLoading = true;
      try {
        const result = await surveyApi.getCalculation(this.id);
        this.calculateResultJSON = JSON.stringify(result, null, 2);
        this.calculateResult = result;
      } catch (e) {
        console.error(e)
      }
      this.calculationLoading = false;
    },
    async onSurveyNotificationChange(value) {
      if (this.survey) {
        this.survey.notifications = value;
        try {
          await surveyApi.saveSurveySubscription(this.survey.key, value);
        } catch (e) {
          this.$handleApiError(e, this.$t('surveys.errors.error_saving_survey_subscription'));
        }
      }
    },
  },
  computed: {
    nameChanged() {
      return this.oldName !== this.survey.name;
    },
    surveyEnded() {
      return dayjs().isAfter(dayjs(this.survey.end));
    },
    totalAnswers() {
      let answers = 0;
      this.statistics.forEach(s => {
        answers = answers + s.answers;
      })
      return answers;
    },
    estimatedAnswerers() {
      return this.survey?.estimatedAnswerers > 0 ? `/ ${this.survey?.estimatedAnswerers}` : ''
    },
    readonly() {
      return this.$isReadOnlyUser;
    }
  },
  async mounted() {
    if (!this.$hasMatchingTenantRoles(['tenant_admin', 'tenant_readonly'])) {
      this.$router.replace({name: 'access_denied'});
      return;
    }
    await this.reloadSurvey();
    await this.reloadTeams();
    this.getCalculation();
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped></style>

<style lang="scss"></style>
